import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';

// Public Pages
import LandingPage from './pages/public/LandingPage';
import LoginPage from './pages/public/LoginPage';
import AdmissionPage from './pages/public/AdmissionPage';
import TrackApplication from './pages/public/TrackApplication';

// Dashboard Pages
import Dashboard from './pages/dashboard/Dashboard';
import ProtectedRoute from './components/common/ProtectedRoute';

// Admin Pages
import UserManagement from './pages/admin/UserManagement';
import StudentManagement from './pages/admin/StudentManagement';
import TeacherManagement from './pages/admin/TeacherManagement';
import ClassManagement from './pages/admin/ClassManagement';
import SubjectManagement from './pages/admin/SubjectManagement';
import AttendanceManagement from './pages/admin/AttendanceManagement';
import ExamManagement from './pages/admin/ExamManagement';
import FeeManagement from './pages/admin/FeeManagement';
import HostelManagement from './pages/admin/HostelManagement';
import SimpleApplicationManagement from './pages/admin/SimpleAdmissionManagement';
import AdmissionManagement from './pages/admin/AdmissionManagement';
import SystemSettings from './pages/admin/SystemSettings';

// Profile Pages
import Profile from './pages/common/Profile';

// Student Pages
import StudentDashboard from './pages/student/StudentDashboard';
import FinanceManagement from './pages/student/FinanceManagement';
import ExamResults from './pages/student/ExamResults';
import ClassSchedule from './pages/student/ClassSchedule';
import Teachers from './pages/student/Teachers';
import Messages from './pages/student/Messages';
import StudentIdCard from './pages/student/StudentIdCard';

// Layout component
import MainLayout from './components/layout/MainLayout';

// Loading component
import LoadingSpinner from './components/common/LoadingSpinner';

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/" 
        element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />} 
      />
      <Route 
        path="/login" 
        element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} 
      />
      <Route path="/admission" element={<AdmissionPage />} />
      <Route path="/track-application" element={<TrackApplication />} />

      {/* Protected Routes with Layout */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <MainLayout>
              {user?.role === 'student' ? <StudentDashboard /> : <Dashboard />}
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/student/dashboard"
        element={
          <ProtectedRoute requiredRole="student">
            <MainLayout>
              <StudentDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/student/finance"
        element={
          <ProtectedRoute requiredRole="student">
            <MainLayout>
              <FinanceManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/student/exam-results"
        element={
          <ProtectedRoute requiredRole="student">
            <MainLayout>
              <ExamResults />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/student/schedule"
        element={
          <ProtectedRoute requiredRole="student">
            <MainLayout>
              <ClassSchedule />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/student/teachers"
        element={
          <ProtectedRoute requiredRole="student">
            <MainLayout>
              <Teachers />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/student/messages"
        element={
          <ProtectedRoute requiredRole="student">
            <MainLayout>
              <Messages />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/student/id-card"
        element={
          <ProtectedRoute requiredRole="student">
            <MainLayout>
              <StudentIdCard />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Profile />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Admin Routes with Layout */}
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requiredRole="admin">
            <MainLayout>
              <UserManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/students"
        element={
          <ProtectedRoute requiredRoles={['admin', 'teacher']}>
            <MainLayout>
              <StudentManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/teachers"
        element={
          <ProtectedRoute requiredRole="admin">
            <MainLayout>
              <TeacherManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/classes"
        element={
          <ProtectedRoute requiredRoles={['admin', 'teacher']}>
            <MainLayout>
              <ClassManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/subjects"
        element={
          <ProtectedRoute requiredRoles={['admin', 'teacher']}>
            <MainLayout>
              <SubjectManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/attendance"
        element={
          <ProtectedRoute requiredRoles={['admin', 'teacher']}>
            <MainLayout>
              <AttendanceManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/exams"
        element={
          <ProtectedRoute requiredRoles={['admin', 'teacher']}>
            <MainLayout>
              <ExamManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/fees"
        element={
          <ProtectedRoute requiredRole="admin">
            <MainLayout>
              <FeeManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/hostels"
        element={
          <ProtectedRoute requiredRole="admin">
            <MainLayout>
              <HostelManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/admissions"
        element={
          <ProtectedRoute requiredRole="admin">
            <MainLayout>
              <SimpleApplicationManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/admission-management"
        element={
          <ProtectedRoute requiredRole="admin">
            <MainLayout>
              <AdmissionManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute requiredRole="admin">
            <MainLayout>
              <SystemSettings />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
